import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Plus, Trash, Pencil, ArrowCounterClockwise, Check, X } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_CALLING_CHIPS = [
  'Call initiated',
  'Called - No Answer',
  'Called - Busy',
  'Called - Switched Off',
  'Call Connected - Interested',
  'Call Connected - Not Interested',
  'Deposit Query',
  'Withdrawal Query'
];

export default function CallingMsgSettings() {
  const [chips, setChips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newChipText, setNewChipText] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingText, setEditingText] = useState('');

  useEffect(() => {
    fetchCallingChips();
  }, []);

  const fetchCallingChips = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings`);
      const savedChips = response.data?.calling_chips;
      if (Array.isArray(savedChips) && savedChips.length > 0) {
        setChips(savedChips);
      } else {
        setChips(DEFAULT_CALLING_CHIPS);
      }
    } catch (err) {
      console.error('Failed to load calling chips:', err);
      setChips(DEFAULT_CALLING_CHIPS);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChips = async (updatedChips) => {
    setSaving(true);
    try {
      const storedUser = localStorage.getItem('dws_user');
      const token = storedUser ? JSON.parse(storedUser)?.token : null;
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.put(`${API}/settings/update`, { calling_chips: updatedChips }, { headers });
      setChips(updatedChips);
      toast.success('Calling Msg chips saved successfully!');
    } catch (err) {
      console.error('Failed to save calling chips:', err);
      toast.error('Failed to save calling chips');
    } finally {
      setSaving(false);
    }
  };

  const handleAddChip = (e) => {
    e.preventDefault();
    const trimmed = newChipText.trim();
    if (!trimmed) {
      toast.error('Chip text cannot be empty');
      return;
    }
    if (chips.includes(trimmed)) {
      toast.error('This chip already exists');
      return;
    }
    const updated = [...chips, trimmed];
    setNewChipText('');
    handleSaveChips(updated);
  };

  const handleDeleteChip = (indexToDelete) => {
    const updated = chips.filter((_, idx) => idx !== indexToDelete);
    handleSaveChips(updated);
  };

  const handleStartEdit = (index, text) => {
    setEditingIndex(index);
    setEditingText(text);
  };

  const handleSaveEdit = (index) => {
    const trimmed = editingText.trim();
    if (!trimmed) {
      toast.error('Chip text cannot be empty');
      return;
    }
    const updated = [...chips];
    updated[index] = trimmed;
    setEditingIndex(null);
    setEditingText('');
    handleSaveChips(updated);
  };

  const handleResetDefaults = () => {
    if (window.confirm('Reset preset calling chips to standard default list?')) {
      handleSaveChips(DEFAULT_CALLING_CHIPS);
    }
  };

  return (
    <div className="bg-white dark:bg-[#111b21] rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-red-50 dark:bg-red-950/50 text-red-600 rounded-xl">
            <Phone size={24} weight="bold" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Calling Msg</h2>
            <p className="text-xs text-slate-400">Manage preset call outcome chips for calling handlers</p>
          </div>
        </div>

        <button
          onClick={handleResetDefaults}
          disabled={saving}
          className="flex items-center gap-1.5 px-3.5 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl text-xs font-semibold transition-colors cursor-pointer"
        >
          <ArrowCounterClockwise size={15} />
          <span>Reset Defaults</span>
        </button>
      </div>

      {/* Add New Chip Form */}
      <form onSubmit={handleAddChip} className="bg-slate-50 dark:bg-slate-800/40 p-4 rounded-xl border border-slate-200 dark:border-slate-700/60 flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={newChipText}
          onChange={(e) => setNewChipText(e.target.value)}
          placeholder="Type new calling outcome message (e.g. Call Connected - Interested)..."
          className="flex-1 px-3.5 py-2 text-xs border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-red-500 focus:outline-none"
        />
        <button
          type="submit"
          disabled={saving || !newChipText.trim()}
          className="flex items-center justify-center gap-1.5 px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs font-bold transition-all shadow-sm disabled:opacity-50 cursor-pointer"
        >
          <Plus size={16} weight="bold" />
          <span>Add Chip</span>
        </button>
      </form>

      {/* Chips List */}
      <div>
        <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
          Quick Outcome Chips ({chips.length})
        </h3>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-3 border-red-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : chips.length === 0 ? (
          <div className="p-8 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
            <p className="text-sm text-slate-400">No calling chips configured yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {chips.map((chip, idx) => (
              <div
                key={idx}
                className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-xl p-3 flex items-center justify-between gap-2 transition-all hover:border-red-300 dark:hover:border-red-900"
              >
                {editingIndex === idx ? (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="text"
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      className="flex-1 px-2.5 py-1 text-xs border border-red-400 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => handleSaveEdit(idx)}
                      className="p-1 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                      title="Save"
                    >
                      <Check size={14} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingIndex(null)}
                      className="p-1 bg-slate-300 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg"
                      title="Cancel"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <>
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
                      {chip}
                    </span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleStartEdit(idx, chip)}
                        className="p-1.5 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteChip(idx)}
                        className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash size={15} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
